import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { SITE_STATUSES, SITE_TYPES, type BulkOperation } from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { useCan } from '../lib/session';
import { ExportButton } from '../components/ExportButton';
import { useTRPC } from '../lib/trpc';
import { zodResolver } from '../lib/zod-resolver';
import { DataTable, type Column } from '../components/DataTable';
import { SiteEditor } from './SiteEditor';
import classes from './EntityPage.module.css';

/**
 * The «МАСОВІ ДІЇ · N САЙТІВ» bar from the mockup, in its order.
 *
 * The mockup draws five buttons and wires only the first; the other four were labels. Each one
 * here carries the operation it stands for into `/bulk`, where the operator still has to run a
 * dry run before anything is applied — the bar chooses the targets and the operation, it does
 * not start the work.
 */
const BULK_ACTIONS: { operation: BulkOperation; label: string }[] = [
  { operation: 'canonical', label: 'Переклеїти canonical' },
  { operation: 'regenerate', label: 'Ре-генерувати контент' },
  { operation: 'translate', label: 'Перекласти' },
  { operation: 'replace_offer', label: 'Замінити офер' },
];

/** «3 САЙТИ», «1 САЙТ», «11 САЙТІВ» — мокап пише лічильник словом, і воно мусить узгоджуватись. */
function plural(count: number): string {
  const tens = count % 100;
  const ones = count % 10;
  if (tens >= 11 && tens <= 14) return 'САЙТІВ';
  if (ones === 1) return 'САЙТ';
  if (ones >= 2 && ones <= 4) return 'САЙТИ';
  return 'САЙТІВ';
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  brand: 'бренд',
  game: 'гра',
  cluster: 'кластер',
};

const createSiteSchema = z.object({
  domain: z.string().trim().min(3, 'Домен — щонайменше 3 символи').max(255),
  geoId: z.string().min(1, 'Оберіть ГЕО'),
  language: z.string().min(1, 'Оберіть мову'),
  entityType: z.enum(['brand', 'game', 'cluster']),
  entityId: z.string().min(1, 'Оберіть сутність'),
  // Optional, and the Select is clearable — clearing one hands the form `null`, which a
  // bare `z.string()` rejects: the whole submit was blocked by an empty optional field.
  hostingPoolId: z.string().nullish(),
});

type CreateSiteValues = z.infer<typeof createSiteSchema>;

const CREATE_SITE_INITIAL: CreateSiteValues = {
  domain: '',
  geoId: '',
  language: '',
  entityType: 'brand',
  entityId: '',
  hostingPoolId: '',
};

interface SiteRow {
  id: string;
  domain: string;
  siteType: string;
  domainType: string;
  status: string;
  geoId: string | null;
  countryCode: string | null;
  language: string | null;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  pagesCount: number;
  indexedPages: number;
  seoScore: number | null;
  linksCount: number;
  abuseFlag: boolean;
  hostingPoolName: string | null;
  lastPingStatus: number | null;
}

const STATUS_LABELS: Record<string, string> = {
  build: 'збірка',
  live: 'live',
  frozen: 'заморожено',
  error: 'помилка',
};

const STATUS_COLOURS: Record<string, string> = {
  live: 'mint',
  build: 'azure',
  frozen: 'amber',
  error: 'rose',
};

const SITE_TYPE_LABELS: Record<string, string> = {
  monobrand: 'монобренд',
  monoslot: 'моно-слот',
  review_cluster: 'ревʼю-кластер',
  drop: 'дроп',
};

/**
 * Реєстр сайтів (§1.5, §11).
 *
 * Every filter is a server-side one. On 5 214 rows a client-side filter is a lie: it narrows
 * the fifty rows that happened to arrive and silently hides everything else, which is exactly
 * how an operator concludes a domain is not in the system.
 */
export function SitesPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [siteTypes, setSiteTypes] = useState<string[]>([]);
  const [geoIds, setGeoIds] = useState<string[]>([]);
  const [abuseOnly, setAbuseOnly] = useState(false);
  const [indexState, setIndexState] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  /**
   * Ticked rows, by id.
   *
   * Kept here rather than in `DataTable` because it has to outlive a refetch and because it is
   * what the mass-action bar hands to `/bulk`. Ids, not rows: a row that scrolled out of the
   * current page is still selected, and the operator sees the count.
   */
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const navigate = useNavigate();
  /** Візард — та сама зона, що й швидке створення: він теж заводить сайт. */
  const canWriteSites = useCan('site_seo', 'write');

  const filters = {
    ...(search ? { search } : {}),
    ...(statuses.length > 0
      ? { statuses: statuses as ('build' | 'live' | 'frozen' | 'error')[] }
      : {}),
    ...(siteTypes.length > 0
      ? { siteTypes: siteTypes as ('monobrand' | 'monoslot' | 'review_cluster' | 'drop')[] }
      : {}),
    ...(geoIds.length > 0 ? { geoIds } : {}),
    ...(abuseOnly ? { abuseOnly: true } : {}),
    ...(indexState === null ? {} : { indexState: indexState as 'full' | 'partial' | 'none' }),
  };

  const list = useQuery(
    trpc.sites.list.queryOptions({ ...filters, pagination: { limit: 100, direction: 'desc' } }),
  );
  const summary = useQuery(trpc.sites.summary.queryOptions(filters));
  const geos = useQuery(
    trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );
  const pools = useQuery(trpc.sites.hostingPools.queryOptions());

  const detail = useQuery({
    ...trpc.sites.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const createForm = useForm<CreateSiteValues>({
    initialValues: CREATE_SITE_INITIAL,
    validate: zodResolver(createSiteSchema),
  });

  const geoLanguages =
    geos.data?.items.find((g) => g.id === createForm.values.geoId)?.languages ?? [];

  // Loaded only once the panel is open and only for the entity type the operator picked —
  // the other two lists would never be looked at, and games alone is 3 180 rows.
  const brandOptions = useQuery({
    ...trpc.brands.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled: createOpen && createForm.values.entityType === 'brand',
  });
  const gameOptions = useQuery({
    ...trpc.games.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled: createOpen && createForm.values.entityType === 'game',
  });
  const clusterOptions = useQuery({
    ...trpc.clusters.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled: createOpen && createForm.values.entityType === 'cluster',
  });

  const entitySelectData =
    createForm.values.entityType === 'brand'
      ? (brandOptions.data?.items ?? []).map((b) => ({ value: b.id, label: b.name }))
      : createForm.values.entityType === 'game'
        ? (gameOptions.data?.items ?? []).map((g) => ({ value: g.id, label: g.name }))
        : (clusterOptions.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }));

  const create = useMutation(
    trpc.sites.create.mutationOptions({
      onSuccess: async () => {
        createForm.reset();
        setCreateOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
      },
    }),
  );

  const columns: Column<SiteRow>[] = [
    {
      key: 'domain',
      header: 'Домен',
      render: (row) => (
        <Group gap={6} wrap="nowrap">
          <span>{row.domain}</span>
          {row.abuseFlag ? (
            <Badge color="rose" size="xs" data-testid={`site-abuse-${row.domain}`}>
              абуз
            </Badge>
          ) : null}
        </Group>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (row) => (
        <Badge
          size="sm"
          color={STATUS_COLOURS[row.status] ?? 'graphite'}
          data-testid={`site-status-${row.domain}`}
        >
          {STATUS_LABELS[row.status] ?? row.status}
        </Badge>
      ),
    },
    {
      key: 'type',
      header: 'Тип',
      render: (row) => (
        <span className={classes.dim}>{SITE_TYPE_LABELS[row.siteType] ?? row.siteType}</span>
      ),
    },
    {
      key: 'entity',
      header: 'Сутність',
      render: (row) => (
        <span data-testid={`site-entity-${row.domain}`}>{row.entityName ?? '—'}</span>
      ),
    },
    {
      key: 'geo',
      header: 'ГЕО · мова',
      render: (row) => (
        <Group gap={6}>
          <Badge size="sm" variant="default" data-testid={`site-geo-${row.domain}`}>
            {row.countryCode ?? '—'}
          </Badge>
          <span className={classes.mono}>{row.language ?? '—'}</span>
        </Group>
      ),
    },
    {
      key: 'pages',
      header: 'Сторінок',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`site-pages-${row.domain}`}>
          {row.pagesCount}
        </span>
      ),
    },
    {
      key: 'seo',
      header: 'SEO',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.seoScore ?? '—'}</span>,
    },
    {
      /*
       * «В ІНДЕКСІ» — колонка мокапа (`siteCols`). Дріб, а не відсоток: «45 / 48» одразу
       * каже, скільки сторінок лишилось, а «94%» тієї ж інформації не несе — і на сайті з
       * трьох сторінок виглядає так само переконливо, як на сайті з трьохсот.
       */
      key: 'indexed',
      header: 'В індексі',
      align: 'right',
      render: (row) => (
        <Text
          className={row.indexedPages === 0 && row.pagesCount > 0 ? classes.danger : classes.mono}
          data-testid={`site-indexed-${row.domain}`}
        >
          {row.indexedPages} / {row.pagesCount}
        </Text>
      ),
    },
  ];

  const site = detail.data;

  return (
    <Stack gap="md">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          label="Домен"
          placeholder="site-0…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="site-search"
          style={{ width: 200 }}
        />
        <MultiSelect
          label="Статус"
          data={SITE_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] ?? s }))}
          value={statuses}
          onChange={setStatuses}
          clearable
          data-testid="site-status-filter"
          style={{ width: 190 }}
        />
        <MultiSelect
          label="Тип"
          data={SITE_TYPES.map((s) => ({ value: s, label: SITE_TYPE_LABELS[s] ?? s }))}
          value={siteTypes}
          onChange={setSiteTypes}
          clearable
          data-testid="site-type-filter"
          style={{ width: 190 }}
        />
        <MultiSelect
          label="ГЕО"
          data={(geos.data?.items ?? []).map((g) => ({
            value: g.id,
            label: `${g.countryCode} · ${g.countryName}`,
          }))}
          value={geoIds}
          onChange={setGeoIds}
          searchable
          clearable
          data-testid="site-geo-filter"
          style={{ width: 210 }}
        />
        <Switch
          size="xs"
          label="Тільки абузні"
          checked={abuseOnly}
          onChange={(e) => {
            setAbuseOnly(e.currentTarget.checked);
          }}
          data-testid="site-abuse-only"
        />
        {/*
          * «В ІНДЕКСІ» з мокапа (`siteFilters`, значення «частково») на місці «без успішного
          * пінга». Пінг каже, що сервер відповідає; індекс каже, що сторінки бачить пошуковик,
          * і саме друге вирішує, чи сайт узагалі працює на нас.
          */}
        <Select
          label="В індексі"
          placeholder="усі"
          data={[
            { value: 'full', label: 'повністю' },
            { value: 'partial', label: 'частково' },
            { value: 'none', label: 'немає' },
          ]}
          value={indexState}
          onChange={setIndexState}
          clearable
          size="xs"
          data-testid="site-index-filter"
          style={{ width: 150 }}
        />
      </Group>

      {summary.data ? (
        <Group gap="lg" data-testid="site-summary">
          <Text className={classes.dim}>
            усього:{' '}
            <span className={classes.mono} data-testid="site-total">
              {summary.data.total}
            </span>
          </Text>
          <Text className={classes.dim}>
            live:{' '}
            <span className={classes.mono} data-testid="site-live">
              {summary.data.byStatus.live ?? 0}
            </span>
          </Text>
          <Text className={classes.dim}>
            абуз:{' '}
            <span className={classes.mono} data-testid="site-abuse-count">
              {summary.data.abuse}
            </span>
          </Text>
          <Text className={classes.dim}>
            в індексі:{' '}
            <span className={classes.mono} data-testid="site-indexed-count">
              {summary.data.indexed}
            </span>
          </Text>
          {/* «5 214 записів · 3 вибрано» — мокап тримає лічильник вибраного поруч із загальним. */}
          <Text className={classes.dim}>
            вибрано:{' '}
            <span className={classes.mono} data-testid="site-selected-count">
              {ticked.size}
            </span>
          </Text>
        </Group>
      ) : null}

      {ticked.size === 0 ? null : (
        <Group gap={7} className={classes.bulkBar} data-testid="site-bulk-bar">
          <Text className={classes.bulkBarLabel}>
            МАСОВІ ДІЇ · {ticked.size} {plural(ticked.size)}
          </Text>
          {BULK_ACTIONS.map((action) => (
            <Button
              key={action.operation}
              variant="default"
              size="compact-sm"
              data-testid={`site-bulk-${action.operation}`}
              onClick={() => {
                void navigate({
                  to: '/bulk',
                  search: { siteIds: [...ticked], operation: action.operation },
                });
              }}
            >
              {action.label}
            </Button>
          ))}
          <Button
            variant="subtle"
            size="compact-sm"
            data-testid="site-bulk-clear"
            onClick={() => {
              setTicked(new Set());
            }}
          >
            Зняти вибір
          </Button>
        </Group>
      )}

      {/*
       * Швидке створення лишається поруч із візардом навмисно: панель заводить рядок за
       * чотири поля, коли домен уже відомий і решту допишуть потім, а візард веде через шість
       * кроків і завершується перевіркою перед деплоєм. Прибрати панель означало б змусити
       * проходити шість кроків заради одного рядка.
       */}
      {canWriteSites ? (
        <Group gap="sm">
          <Button
            size="compact-xs"
            variant="default"
            data-testid="site-wizard-open"
            onClick={() => {
              void navigate({ to: '/sites/new' });
            }}
          >
            Новий сайт — візард
          </Button>
        </Group>
      ) : null}

      <CreatePanel
        onReset={() => {
          create.reset();
          createForm.reset();
        }}
        zone="site_seo"
        label="Додати сайт"
        testId="site"
        open={createOpen}
        onOpenChange={setCreateOpen}
        busy={create.isPending}
        error={create.error}
        onSubmit={createForm.onSubmit((values) => {
          create.mutate({
            domain: values.domain,
            geoId: values.geoId,
            language: values.language,
            entityType: values.entityType,
            entityId: values.entityId,
            ...(values.hostingPoolId ? { hostingPoolId: values.hostingPoolId } : {}),
          });
        })}
      >
        <TextInput
          label="Домен"
          size="xs"
          placeholder="site-042.example"
          data-testid="create-site-domain"
          style={{ width: 200 }}
          {...createForm.getInputProps('domain')}
        />
        <Select
          label="ГЕО"
          size="xs"
          placeholder="оберіть"
          searchable
          data={(geos.data?.items ?? []).map((g) => ({
            value: g.id,
            label: `${g.countryCode} · ${g.countryName}`,
          }))}
          value={createForm.values.geoId}
          onChange={(value) => {
            createForm.setFieldValue('geoId', value ?? '');
            createForm.setFieldValue('language', '');
          }}
          error={createForm.errors.geoId}
          data-testid="create-site-geo"
          style={{ width: 190 }}
        />
        <Select
          label="Мова"
          size="xs"
          placeholder={createForm.values.geoId === '' ? 'спершу ГЕО' : 'оберіть'}
          disabled={createForm.values.geoId === ''}
          data={geoLanguages.map((l) => ({ value: l, label: l }))}
          data-testid="create-site-language"
          style={{ width: 110 }}
          value={createForm.values.language}
          /*
           * Явні `value`/`onChange`/`error` замість `getInputProps`, як на полі ГЕО поруч:
           * `allowDeselect` у Mantine 9 увімкнений, тож повторний клік по обраному варіанту
           * віддає `null`, а схема чекає рядок — оператор отримував англійське
           * «expected string, received null» замість підпису українською.
           */
          onChange={(value) => {
            createForm.setFieldValue('language', value ?? '');
          }}
          error={createForm.errors.language}
        />
        <Select
          label="Тип сутності"
          size="xs"
          allowDeselect={false}
          data={Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          value={createForm.values.entityType}
          onChange={(value) => {
            if (value === 'brand' || value === 'game' || value === 'cluster') {
              createForm.setFieldValue('entityType', value);
              createForm.setFieldValue('entityId', '');
            }
          }}
          data-testid="create-site-entity-type"
          style={{ width: 130 }}
        />
        <Select
          label="Сутність"
          size="xs"
          placeholder="оберіть"
          searchable
          data={entitySelectData}
          data-testid="create-site-entity"
          style={{ width: 220 }}
          value={createForm.values.entityId}
          onChange={(value) => {
            createForm.setFieldValue('entityId', value ?? '');
          }}
          error={createForm.errors.entityId}
        />
        <Select
          label="Хостинг-пул"
          size="xs"
          placeholder="без пулу"
          clearable
          data={(pools.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
          data-testid="create-site-hosting-pool"
          style={{ width: 170 }}
          {...createForm.getInputProps('hostingPoolId')}
        />
      </CreatePanel>

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Сайтів немає під ці фільтри. Зніміть частину фільтрів або додайте сайт кнопкою вище."
        onRowClick={(row) => {
          // Картка сайту тепер має власний маршрут: її можна надіслати колезі, і шість
          // вкладок мокапа живуть там, а не панеллю під реєстром.
          void navigate({ to: '/sites/$siteId', params: { siteId: row.id } });
        }}
        rowTestId={(row) => `site-row-${row.domain}`}
        testId="site-table"
        selection={{
          idOf: (row) => row.id,
          selected: ticked,
          onChange: setTicked,
        }}
        footnote={`1–${String(list.data?.items.length ?? 0)} з ${String(summary.data?.total ?? 0)}`}
        /*
         * «Експорт CSV» мокапа. Порожня вибірка — увесь реєстр у межах прав; тикнуті рядки —
         * «експортувати вибране», та сама вибірка, якою оператор щойно скористався.
         */
        footerActions={<ExportButton dataset="sites" zone="site_seo" ids={[...ticked]} />}
      />

      {/*
       * Картка редагування над інформаційною панеллю: тех-шар сайта писався **лише** масовими
       * операціями, тобто виправити один домен означало запустити масову операцію на один
       * домен — із сухим прогоном, діффом і гейтом двох пар очей.
       */}
      {site === undefined ? null : (
        <SiteEditor
          key={site.id}
          row={{
            id: site.id,
            domain: site.domain,
            status: site.status,
            canonicalTarget: site.canonicalTarget,
            canonicalSiteId: site.canonicalSiteId,
            hreflangRole: site.hreflangRole,
            pairedSiteId: site.pairedSiteId,
            templateId: site.templateId,
            hostingPoolName: site.hostingPoolName,
          }}
          onClose={() => {
            setSelectedId(null);
          }}
        />
      )}

      {selectedId === null ? null : (
        <Box className={classes.panel} data-testid="site-detail">
          {detail.isPending ? (
            <Loader size="sm" />
          ) : site === undefined ? null : (
            <Stack gap="md">
              <Group justify="space-between">
                <Text className={classes.panelTitle}>{site.domain}</Text>
                <Group gap={8}>
                  <Badge size="sm" color={STATUS_COLOURS[site.status] ?? 'graphite'}>
                    {STATUS_LABELS[site.status] ?? site.status}
                  </Badge>
                  {site.pairedDomain === null ? null : (
                    <Badge size="sm" variant="default" data-testid="site-paired">
                      пара: {site.pairedDomain}
                    </Badge>
                  )}
                </Group>
              </Group>

              <Group gap="lg" wrap="wrap">
                <Text className={classes.dim}>
                  сутність <span>{site.entityName ?? '—'}</span>
                </Text>
                <Text className={classes.dim}>
                  пул <span>{site.hostingPoolName ?? '—'}</span>
                </Text>
                <Text className={classes.dim}>
                  реєстратор <span>{site.registrarAccountName ?? '—'}</span>
                </Text>
                <Text className={classes.dim}>
                  canonical <span className={classes.mono}>{site.canonicalTarget}</span>
                </Text>
                <Text className={classes.dim}>
                  hreflang <span className={classes.mono}>{site.hreflangRole}</span>
                </Text>
                {/*
                 * Мовна група — те, що сайт справді пише в опублікованому HTML. Група
                 * запікається в байти на момент рендера, тож побратим, який став live
                 * пізніше, у ній відсутній; без цього рядка дізнатись про це можна було б
                 * лише перечитавши HTML сайту руками.
                 */}
                <Text className={classes.dim}>
                  група{' '}
                  <span className={classes.mono} data-testid="site-hreflang-group">
                    {site.hreflangGroup.peers.length === 0
                      ? 'сама'
                      : site.hreflangGroup.peers.map((peer) => peer.domain).join(', ')}
                  </span>
                </Text>
                {site.hreflangGroup.stale ? (
                  <Text className={classes.warn} data-testid="site-hreflang-stale">
                    опубліковану групу застаріло: побратим став live після цього деплою —
                    задеплойте сайт ще раз
                  </Text>
                ) : null}
                <Text className={classes.dim}>
                  ключів <span className={classes.mono}>{site.keywordCount}</span>
                </Text>
              </Group>

              <Box>
                <Text className={classes.dim} mb={6}>
                  Сторінки
                </Text>
                {site.pages.length === 0 ? (
                  <Text className={classes.danger} data-testid="site-no-pages">
                    Сторінок немає — сайт ще не збирався
                  </Text>
                ) : (
                  <Table data-testid="site-pages">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>url</Table.Th>
                        <Table.Th>тип</Table.Th>
                        <Table.Th>слів</Table.Th>
                        <Table.Th>ключів</Table.Th>
                        <Table.Th>статус</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {site.pages.map((pageRow) => (
                        <Table.Tr key={pageRow.id} data-testid={`site-page-${pageRow.url}`}>
                          <Table.Td className={classes.mono}>{pageRow.url}</Table.Td>
                          <Table.Td className={classes.dim}>{pageRow.pageType}</Table.Td>
                          <Table.Td className={classes.mono}>{pageRow.words}</Table.Td>
                          <Table.Td className={classes.mono}>{pageRow.keywordsCount}</Table.Td>
                          <Table.Td>
                            <Badge size="sm" variant="default">
                              {pageRow.status}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      )}

      {pools.data === undefined ? null : (
        <Box className={classes.panel} data-testid="site-pools">
          <Text className={classes.panelTitle} mb={8}>
            Хостинг-пули
          </Text>
          <Group gap="md" wrap="wrap">
            {pools.data.map((pool) => (
              <Group key={pool.id} gap={8} data-testid={`pool-${pool.name}`}>
                <Text size="sm">{pool.name}</Text>
                <Text
                  className={pool.utilisationPct >= 90 ? classes.danger : classes.mono}
                  data-testid={`pool-util-${pool.name}`}
                >
                  {pool.sitesCount}/{pool.capacity}
                </Text>
              </Group>
            ))}
          </Group>
        </Box>
      )}
    </Stack>
  );
}
